import React, { useEffect, useRef, useState } from 'react';
import { array, bool, func, number, object, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { propTypes } from '../../util/types';
import { sendVerificationEmail, hasCurrentUserErrors } from '../../ducks/user.duck';
import {
  logout,
  authenticationInProgress,
  signup,
  login,
  isAuthenticatedSelector,
} from '../../ducks/auth.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { IdlePromptPopup, Modal, Topbar } from '../../components';
import SignupForm from '../AuthenticationPage/SignupForm/SignupForm';
import LoginForm from '../AuthenticationPage/LoginForm/LoginForm';
import { getDreamHomePageData } from '../../util/dreamHomePageHelpers';
import TermsAndConditions from '../AuthenticationPage/TermsAndConditions/TermsAndConditions';
import { FormattedMessage, useIntl } from 'react-intl';
import { DreamHomePageTab, StringBoolean } from '../../util/enums';
import { createResourceLocatorString } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { hasCurrentUser } from '../../util/genericHelpers';
import Icons from '../../components/Icons/Icons';
import css from './TopbarContainer.module.css';

export const TopbarContainerComponent = props => {
  const {
    authInProgress,
    currentPage,
    currentSearchParams,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    history,
    authScopes,
    hasGenericError,
    location,
    notificationCount,
    onLogout,
    onManageDisableScrolling,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    onResendVerificationEmail,
    isDreamHomeTopbar,
    isAuthenticationPage,
    submitSignup,
    submitLogin,
    ...rest
  } = props;
  const isAuthPage =
    ['/login', '/signup'].includes(location.pathname) ||
    location.pathname.endsWith('/contact-supplier');
  const intl = useIntl();
  const routeConfiguration = useRouteConfiguration();
  const isCurrentUser = hasCurrentUser(currentUser);
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const { content, delay = 0 } = props;
  const isStartYourProjectPage = location.pathname === '/startyourproject';
  const [show, setShow] = useState(false);
  const inactivityTimeout = useRef(null);
  const handleSubmitSignup = async values => {
    const dreamHomePageData = getDreamHomePageData();
    const listingParams = {
      ...dreamHomePageData,
    };

    const { fname, lname, ...rest } = values;

    // Check if this is a supplier or customer signup based on current path
    const isSupplierSignup = location.pathname === '/signup-supplier';
    const isCustomerSignup = location.pathname === '/signup-customer';

    // Map supplier signup user types to core user types
    let mappedUserType = rest.userType;
    if (isSupplierSignup && rest.userType) {
      const supplierTypeMapping = {
        'offsiteSupplier': 'offsiteSupplier',
        'builder': 'offsiteSupplier',
        'architect': 'architectProfessional',
        'professionalServices': 'architectProfessional',
      };
      mappedUserType = supplierTypeMapping[rest.userType] || rest.userType;
    }

    const params = {
      firstName: fname.trim(),
      lastName: lname.trim(),
      ...rest,
      ...(mappedUserType !== rest.userType ? { userType: mappedUserType } : {}),
    };

    // Store supplier signup flag for post-verification redirect
    if (isSupplierSignup) {
      params.isSupplierSignup = true;
    }

    // Store customer signup flag for post-verification redirect
    if (isCustomerSignup) {
      params.isCustomerSignup = true;
    }

    // Attach PP ref from StartYourProject page if present
    const PP_REF_SESSION_KEY = 'startYourProject_ppRef';
    try {
      const ppRef = sessionStorage.getItem(PP_REF_SESSION_KEY);
      if (ppRef) {
        params.planningReference = ppRef;
        sessionStorage.removeItem(PP_REF_SESSION_KEY);
      }
    } catch (e) {}

    try {
      const resp = await post('/api/mail_octopus/user', {
        email: values.email,
        shouldCreateNew: values.subscribedToDesignAlerts === 'yes',
        subscriptionStatus:
          values.subscribedToDesignAlerts === 'yes' ? 'SUBSCRIBED' : 'UNSUBSCRIBED',
        firstName: fname,
        lastName: lname,
      });

      const designAlertsId = resp?.id;
      const newParams = { ...params, designAlertsId };
      await submitSignup(newParams, listingParams);

      history.push(
        createResourceLocatorString('DreamHomePage', routeConfiguration, {
          tab: DreamHomePageTab.DESIGN_BRIEF,
        })
      );
    } catch (error) {
      return await submitSignup(params, listingParams);
    }
  };

  const handleClose = () => {
    setShow(false);
    clearTimeout(inactivityTimeout.current);
    inactivityTimeout.current = setTimeout(() => {
      setShow(true);
    }, 5000 + delay);
  };

  const handleSubmitLogin = values => {
    const { email, password } = values;
    return submitLogin(email, password);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Save user auth state to localStorage using consistent enum values
    if (isAuthenticated) {
      localStorage.setItem('userIsAuthenticated', StringBoolean.TRUE);
    } else {
      localStorage.removeItem('userIsAuthenticated');
    }
  }, [isAuthenticated]);
  return (
    <>
      <Topbar
        authInProgress={authInProgress}
        currentPage={currentPage}
        currentSearchParams={currentSearchParams}
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        history={history}
        isAuthenticated={isAuthenticated}
        authScopes={authScopes}
        location={location}
        notificationCount={notificationCount}
        onLogout={onLogout}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
        showGenericError={hasGenericError}
        isDreamHomeTopbar={isDreamHomeTopbar}
        isAuthenticationPage={isAuthenticationPage}
        {...rest}
      />
      <Modal
        id="idlePromptModal"
        isOpen={show && !isCurrentUser}
        onClose={handleClose}
        onManageDisableScrolling={onManageDisableScrolling}
        isIdlePromptModal={true}
      >
        <div className={css.authContentWrapper}>
          <div className={css.authContentImg}>
            <div className={css.authContentImgDetails}>
              <Icons name="stars" />
              {location.pathname === '/signup-supplier' ? (
                <>
                  <h1>
                    Join the UK's <span>Fastest-Growing</span> Construction Marketplace
                  </h1>
                  <p>
                    Showcase your work to self-builders and developers — upload your first 5 case studies completely free.
                  </p>
                </>
              ) : location.pathname === '/signup-customer' ? (
                <>
                  <h1>
                    Start Your Build With <span>Confidence</span>
                  </h1>
                  <p>
                    Create a free profile and complete your project brief so we can match you with some builders and suppliers
                  </p>
                </>
              ) : (
                <>
                  <h1>
                    <FormattedMessage id="TopbarContainer.join" />
                    <span>
                      {' '}
                      <FormattedMessage id="TopbarContainer.countLabel" />
                    </span>{' '}
                    <FormattedMessage id="TopbarContainer.states" />
                  </h1>
                  <p>
                    <FormattedMessage id="TopbarContainer.joinNow" />{' '}
                  </p>
                </>
              )}
            </div>
          </div>
          <div className={css.authContentInfo}>
            <SignupForm
              onSubmit={handleSubmitSignup}
              inProgress={false}
              termsAndConditions={<TermsAndConditions intl={intl} />}
              isSupplierSignup={location.pathname === '/signup-supplier'}
              isCustomerSignup={location.pathname === '/signup-customer'}
            />
          </div>
        </div>
      </Modal>
      {!isAuthenticated && !isAuthPage && (
        <IdlePromptPopup
          delay={isStartYourProjectPage ? 30000 : 0}
          suppressOnMouseMove={false}
          suppressOnFocus={isStartYourProjectPage ? ['#ppRefInput'] : []}
          onManageDisableScrolling={onManageDisableScrolling}
          content={
            isStartYourProjectPage ? (
              <SignupForm
                onSubmit={handleSubmitSignup}
                inProgress={false}
                termsAndConditions={<TermsAndConditions intl={intl} />}
                isCustomerSignup={true}
              />
            ) : (
              <LoginForm
                onSubmit={handleSubmitLogin}
                inProgress={authInProgress}
              />
            )
          }
        />
      )}
      {/* <Modal
        isOpen={true}
        onClose={handleClose}
        onManageDisableScrolling={onManageDisableScrolling}
        isStatusModal={true}
      >
        <CostEstimationReminderPopup delay={0} />
      </Modal> */}
    </>
  );
};

TopbarContainerComponent.defaultProps = {
  currentPage: null,
  currentSearchParams: null,
  currentUser: null,
  currentUserHasOrders: null,
  notificationCount: 0,
  sendVerificationEmailError: null,
  authScopes: null,
};

TopbarContainerComponent.propTypes = {
  authInProgress: bool.isRequired,
  currentPage: string,
  currentSearchParams: object,
  currentUser: propTypes.currentUser,
  currentUserHasListings: bool.isRequired,
  currentUserHasOrders: bool,
  isAuthenticated: bool.isRequired,
  authScopes: array,
  notificationCount: number,
  onLogout: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  onResendVerificationEmail: func.isRequired,
  hasGenericError: bool.isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({ state: object }).isRequired,
};

const mapStateToProps = state => {
  // Topbar needs isAuthenticated
  const { isAuthenticated, logoutError, authScopes } = state.auth;
  // Topbar needs user info.
  const {
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentUserNotificationCount: notificationCount,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
  } = state.user;
  const hasGenericError = !!(logoutError || hasCurrentUserErrors(state));
  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    notificationCount,
    isAuthenticated,
    authScopes,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    hasGenericError,
  };
};

const mapDispatchToProps = dispatch => ({
  onLogout: historyPush => dispatch(logout(historyPush)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  submitSignup: (params, listingParams) => dispatch(signup(params, listingParams)),
  submitLogin: (email, password) => dispatch(login(email, password)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const TopbarContainer = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(TopbarContainerComponent);

export default TopbarContainer;
